import React, { useCallback, useRef, useState } from 'react';
import { Box, Button, Dialog, DialogContent } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { TrackTutorialProgressAPI } from '../../api/API';
import useLessonTracking from '../../pages/system/tutorials/useLessonTracking';
import { lessonVersion } from '../../pages/system/tutorials/useTutorialProgress';
import ColorPalette from '../../colorpalette/ColorPalette';

const captionCueSx = {
  backgroundColor: 'rgba(26, 26, 46, 0.78)',
  color: '#FFFFFF',
  fontFamily: 'inherit',
  fontSize: '0.95em',
  lineHeight: 1.4
};

const nextButtonSx = {
  textTransform: 'none',
  fontWeight: 700,
  fontSize: '1rem',
  borderRadius: '12px',
  px: 3.5,
  py: 1.25,
  background: `linear-gradient(135deg, ${ColorPalette.blueMain} 0%, #4A6BFF 100%)`,
  boxShadow: '0 8px 20px rgba(90, 128, 251, 0.28)',
  '&:hover': { background: `linear-gradient(135deg, #4A6BFF 0%, ${ColorPalette.blueMain} 100%)` }
};

/**
 * Aula do guia (item "cores da agenda"): só o vídeo e o botão Próximo. O Próximo
 * conclui o passo mesmo sem dar play: grava a abertura da lição (evento "open" de
 * tutorials/track.php, o mesmo que o play grava e que FirstSteps lê) e devolve
 * o controle ao guia. Fora do guia a aula continua no TutorialDialog.
 * @param {Object} lesson - lição de tutorials.json
 * @param {Function} onNext - chamado depois de gravar a abertura
 */
function GuidedTourVideo({ lesson, onNext }) {
  const fullScreen = useMediaQuery('(max-width: 768px)');
  const [saving, setSaving] = useState(false);
  const opened = useRef(false);

  const onTrack = useCallback((item, event, data = {}) => {
    if (event === 'open') opened.current = true;
    TrackTutorialProgressAPI({ lesson_id: item.id, version: lessonVersion(item), event, ...data }, () => {});
  }, []);
  const tracking = useLessonTracking({ lesson, playKey: lesson.id, progress: null, source: 'guide', onTrack });

  const next = () => {
    if (opened.current) { onNext(); return; }
    setSaving(true);
    opened.current = true;
    TrackTutorialProgressAPI({ lesson_id: lesson.id, version: lessonVersion(lesson), event: 'open', source: 'guide' }, () => {
      setSaving(false);
      onNext();
    });
  };

  const media = lesson.media || null;

  return (
    <Dialog
      open
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      aria-label={lesson.title}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : '20px' } }}
    >
      <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
        <Box
          sx={{
            aspectRatio: '16 / 9',
            borderRadius: '16px',
            overflow: 'hidden',
            border: `1px solid ${ColorPalette.border}`,
            backgroundColor: ColorPalette.bgMain,
            '& video::cue': captionCueSx
          }}
        >
          {media && media.type === 'image' && (
            <img src={media.src} alt={lesson.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          )}
          {media && media.type !== 'image' && (
            <video
              src={media.src}
              poster={media.poster}
              preload="metadata"
              controls
              {...tracking}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            >
              {media.captions && <track kind="subtitles" src={media.captions} srcLang="pt-BR" label="Português" default />}
            </video>
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2.5 }}>
          <Button variant="contained" onClick={next} disabled={saving} endIcon={<ArrowForwardIcon />} sx={nextButtonSx}>
            Próximo
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default GuidedTourVideo;
